import React, { useState, useEffect } from 'react';
import { clientsAPI } from '../services/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, RefreshCw, CheckCircle, Phone, Trash2, AlertTriangle } from 'lucide-react';

const PhoneCollisionsPanel = () => {
  const [collisions, setCollisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dupScan, setDupScan] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  const fetchCollisions = async () => {
    setLoading(true);
    try {
      const response = await clientsAPI.getPhoneCollisions();
      setCollisions(response.data.collisions || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const scanDuplicates = async () => {
    setScanning(true);
    try {
      const response = await clientsAPI.scanDuplicates();
      setDupScan(response.data);
    } catch (error) {
    } finally {
      setScanning(false);
    }
  };

  const cleanupDuplicates = async () => {
    if (!window.confirm(`This will permanently remove ${dupScan?.total_duplicates || 0} duplicate client records, keeping the best record for each group. Continue?`)) return;
    setCleaning(true);
    try {
      await clientsAPI.cleanupDuplicates();
      // Re-scan after a short delay
      setTimeout(async () => {
        await scanDuplicates();
        await fetchCollisions();
        setCleaning(false);
      }, 3000);
    } catch (error) {
      setCleaning(false);
    }
  };

  useEffect(() => {
    fetchCollisions();
    scanDuplicates();
  }, []);

  return (
    <div className="space-y-6">
      {/* Duplicate Cleanup Section */}
      <Card className="bg-white border border-gray-100" data-testid="duplicate-cleanup-card">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Duplicate Cleanup</h3>
              <p className="text-gray-500 text-sm">
                Clients sharing the same phone number AND name — safe to merge into one record.
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={scanDuplicates} disabled={scanning}>
                <RefreshCw size={16} className={`mr-2 ${scanning ? 'animate-spin' : ''}`} /> Scan
              </Button>
            </div>
          </div>
        </div>

        {scanning ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-[#4F46E5]" size={24} />
            <span className="ml-3 text-gray-500">Scanning for duplicates...</span>
          </div>
        ) : dupScan ? (
          <div className="p-6">
            {dupScan.total_duplicates === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="mx-auto text-green-400 mb-3" size={40} />
                <p className="text-gray-500">No duplicates found</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-100">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="text-amber-500" size={20} />
                    <div>
                      <p className="font-medium text-amber-800">
                        {dupScan.total_duplicates} duplicate records found in {dupScan.total_groups} groups
                      </p>
                      <p className="text-sm text-amber-600">
                        These are clients with identical phone + name that can be safely merged
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={cleanupDuplicates}
                    disabled={cleaning}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="cleanup-duplicates-btn"
                  >
                    {cleaning ? <Loader2 className="animate-spin mr-2" size={16} /> : <Trash2 size={16} className="mr-2" />}
                    Remove {dupScan.total_duplicates} Duplicates
                  </Button>
                </div>

                <div className="text-sm text-gray-500 mb-2">Top duplicate groups:</div>
                <div className="max-h-64 overflow-y-auto border rounded-lg divide-y divide-gray-100">
                  {dupScan.groups.map((g, i) => (
                    <div key={i} className="flex items-center justify-between p-3 hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">{g.count}x</span>
                        <span className="font-medium text-gray-900">{g.name}</span>
                        <span className="text-sm text-gray-500 font-mono">{g.phone}</span>
                      </div>
                      <span className="text-xs text-gray-400">{g.duplicates_to_remove} to remove</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </Card>

      {/* Phone Collisions Section */}
      <Card className="bg-white border border-gray-100" data-testid="phone-collisions-card">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Phone Collisions</h3>
              <p className="text-gray-500 text-sm">
                Different clients sharing the same phone — may cause incorrect order matching.
                {collisions.length > 0 && <span className="font-medium text-amber-600"> {collisions.length} found</span>}
              </p>
            </div>
            <Button variant="outline" onClick={fetchCollisions} disabled={loading}>
              <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-[#4F46E5]" size={24} />
          </div>
        ) : collisions.length === 0 ? (
          <div className="p-12 text-center">
            <CheckCircle className="mx-auto text-green-400 mb-3" size={40} />
            <p className="text-gray-500">No phone collisions detected</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {collisions.map((collision) => (
              <div key={collision.phone} className="p-4 hover:bg-gray-50">
                <div className="flex items-center gap-3 mb-3">
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">
                    {collision.count} clients
                  </span>
                  <Phone size={14} className="text-gray-400" />
                  <span className="font-mono text-sm text-gray-700">{collision.phone}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {collision.clients.map((client) => (
                    <div
                      key={client.id}
                      className="flex items-center gap-3 p-2 rounded-lg bg-gray-50 border border-gray-100 cursor-pointer hover:border-[#4F46E5]/30"
                      onClick={() => window.open(`/clients/${client.id}`, '_blank')}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">{client.name}</p>
                        <p className="text-xs text-gray-500">
                          WC: {client.woo_customer_id || '\u2014'} · {client.orders || 0} orders
                          {client.last_order_source && (
                            <span className="ml-1 text-[#4F46E5]">· {client.last_order_source}</span>
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default PhoneCollisionsPanel;
